import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationApi, membersApi, invitationsApi } from '../services/organizationApi';
import type {
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  InviteMemberRequest,
  OrganizationRole,
  UpdateMfaPolicyRequest,
} from '../types/organization';

// Query keys
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: () => [...organizationKeys.lists()] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  members: (orgId: string) => [...organizationKeys.detail(orgId), 'members'] as const,
  memberProjectRoles: (orgId: string, userId: string) =>
    [...organizationKeys.members(orgId), userId, 'project-roles'] as const,
  invitations: (orgId: string) => [...organizationKeys.detail(orgId), 'invitations'] as const,
};

export const invitationKeys = {
  all: ['invitations'] as const,
  preview: (token: string) => [...invitationKeys.all, 'preview', token] as const,
};

// ============ Query Hooks ============

export function useOrganizations() {
  return useQuery({
    queryKey: organizationKeys.list(),
    queryFn: async () => {
      const response = await organizationApi.list();
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch organizations');
      }
      return response.data ?? [];
    },
  });
}

export function useOrganization(orgId: string | undefined) {
  return useQuery({
    queryKey: organizationKeys.detail(orgId!),
    queryFn: async () => {
      const response = await organizationApi.get(orgId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch organization');
      }
      return response.data!;
    },
    enabled: !!orgId,
  });
}

export function useMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: organizationKeys.members(orgId!),
    queryFn: async () => {
      const response = await membersApi.list(orgId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch members');
      }
      return response.data ?? [];
    },
    enabled: !!orgId,
  });
}

export function useInvitationPreview(token: string | undefined) {
  return useQuery({
    queryKey: invitationKeys.preview(token!),
    queryFn: async () => {
      const response = await invitationsApi.getPreview(token!);
      if (!response.success) {
        throw new Error(response.error || 'Invitation not found');
      }
      return response.data!;
    },
    enabled: !!token,
    retry: false, // Don't retry on invalid tokens
  });
}

// ============ Mutation Hooks ============

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateOrganizationRequest) => {
      const response = await organizationApi.create(data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create organization');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, data }: { orgId: string; data: UpdateOrganizationRequest }) => {
      const response = await organizationApi.update(orgId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update organization');
      }
      return response.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orgId: string) => {
      const response = await organizationApi.delete(orgId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete organization');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, data }: { orgId: string; data: InviteMemberRequest }) => {
      const response = await membersApi.invite(orgId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to send invitation');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.invitations(variables.orgId) });
    },
  });
}

export function useInvitations(orgId: string | undefined) {
  return useQuery({
    queryKey: organizationKeys.invitations(orgId!),
    queryFn: async () => {
      const response = await invitationsApi.list(orgId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch invitations');
      }
      return response.data ?? [];
    },
    enabled: !!orgId,
  });
}

export function useCancelInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, invitationId }: { orgId: string; invitationId: string }) => {
      const response = await invitationsApi.cancel(orgId, invitationId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to cancel invitation');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.invitations(variables.orgId) });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, invitationId }: { orgId: string; invitationId: string }) => {
      const response = await invitationsApi.resend(orgId, invitationId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to resend invitation');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.invitations(variables.orgId) });
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      userId,
      role,
    }: {
      orgId: string;
      userId: string;
      role: OrganizationRole;
    }) => {
      const response = await membersApi.updateRole(orgId, userId, { role });
      if (!response.success) {
        throw new Error(response.error || 'Failed to update role');
      }
      return response.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.orgId) });
    },
  });
}

export function useMemberProjectRoles(orgId: string | undefined, userId: string | undefined) {
  return useQuery({
    queryKey: organizationKeys.memberProjectRoles(orgId!, userId!),
    queryFn: async () => {
      const response = await membersApi.listProjectRoles(orgId!, userId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch project roles');
      }
      return response.data ?? [];
    },
    enabled: !!orgId && !!userId,
  });
}

export function useSetMemberProjectRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      userId,
      scope,
      role,
    }: {
      orgId: string;
      userId: string;
      scope: string;
      role: OrganizationRole;
    }) => {
      const response = await membersApi.setProjectRole(orgId, userId, scope, { role });
      if (!response.success) {
        throw new Error(response.error || 'Failed to assign project role');
      }
      return response.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.memberProjectRoles(variables.orgId, variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.orgId) });
    },
  });
}

export function useRemoveMemberProjectRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      userId,
      scope,
    }: {
      orgId: string;
      userId: string;
      scope: string;
    }) => {
      const response = await membersApi.removeProjectRole(orgId, userId, scope);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove project role');
      }
      return response;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.memberProjectRoles(variables.orgId, variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.orgId) });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, userId }: { orgId: string; userId: string }) => {
      const response = await membersApi.remove(orgId, userId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove member');
      }
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.orgId) });
    },
  });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await invitationsApi.accept(token);
      if (!response.success) {
        throw new Error(response.error || 'Failed to accept invitation');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, file }: { orgId: string; file: File }) => {
      const response = await organizationApi.uploadLogo(orgId, file);
      if (!response.success) {
        throw new Error(response.error || 'Failed to upload logo');
      }
      return response.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

export function useRemoveLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orgId: string) => {
      const response = await organizationApi.removeLogo(orgId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove logo');
      }
      return response.data!;
    },
    onSuccess: (_data, orgId) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

export function useUpdateMfaPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, data }: { orgId: string; data: UpdateMfaPolicyRequest }) => {
      const response = await organizationApi.updateMfaPolicy(orgId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update MFA policy');
      }
      return response.data!.mfa_policy;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.orgId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.orgId) });
    },
  });
}
